import { Component, Input, OnInit, OnChanges, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Resource } from '../../models/resource';
import { ResourceService } from '../../services/resource.service';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'app-resource-song',
  templateUrl: './resource-song.component.html',
  styleUrls: ['./resource-song.component.scss'],
  standalone: false
})
export class ResourceSongComponent implements OnInit, OnChanges {
  @Input() songId!: number;
  @Input() viewMode = true;
  @Input() resourceFolder!: string; // Receive the ResourceFolder field from the parent SongComponent
  @ViewChild('resourceFormCard', { static: false, read: ElementRef }) resourceFormCard?: ElementRef<HTMLElement>;
  
  resources: Resource[] = [];
  selectedResource: Resource | null = null;
  resourceForm!: FormGroup;
  showResourceForm = false;
  selectedFile: File | null = null;
  currentFile: string | null = null; // Track the current file when editing

  constructor(
    private resourceService: ResourceService,
    private fb: FormBuilder,
    private http: HttpClient
  ) { }

  ngOnInit(): void {
    this.initForm();
    if (this.songId) {
      this.loadResources();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['songId'] && !changes['songId'].firstChange && this.songId) {
      this.loadResources();
    }
  }

  private initForm(): void {
    this.resourceForm = this.fb.group({
      name: ['', Validators.required], // Resource Name
      resourceType: ['', Validators.required], // Resource Type
      isHyperlink: [false], // Is Hyperlink
      path: [{ value: '', disabled: true }], // Path, initially disabled
      songId: [null], // Will be set dynamically
      composerId: [null], // Optional field
      eventId: [null] // Optional field
    });
  }

  loadResources(): void {
    console.log('Loading resources for songId:', this.songId);
    this.resourceService.getResourcesBySong(this.songId)
      .subscribe({
        next: (resources) => {
          console.log('Resources loaded:', resources);
          this.resources = resources;
        },
        error: (error) => {
          console.error('Error loading resources:', error);
        }
      });
  }

  addResource(): void {
    this.selectedResource = null;
    this.selectedFile = null;
    this.currentFile = null;
    this.resourceForm.reset({
      isHyperlink: false
    });
    this.showResourceForm = true;

    // Wait for the form to render before scrolling to it.
    setTimeout(() => this.scrollToResourceForm(), 50);
  }

  editResource(resource: Resource): void {
    this.selectedResource = resource;
    this.showResourceForm = true;
    this.selectedFile = null; // Reset file selection when editing
    
    // Extract the filename from the resource path if it exists
    if (resource.path && !resource.isHyperlink) {
      this.currentFile = resource.path.split('/').pop() || null;
    } else {
      this.currentFile = null;
    }
    
    this.resourceForm.patchValue({
      name: resource.name,
      //path: resource.path,
      isHyperlink: resource.isHyperlink,
      resourceType: resource.resourceType,
      songId: resource.songId,
      composerId: resource.composerId,
      eventId: resource.eventId
    });

    // Enable or disable the path control based on the isHyperlink value
    if (resource.isHyperlink) {
      this.resourceForm.get('path')?.enable();
    } else {
      this.resourceForm.get('path')?.disable();
    }
    
    // Wait for the form to render before scrolling to it.
    setTimeout(() => this.scrollToResourceForm(), 50);
  }

  deleteResource(id: number): void {
    if (confirm('Are you sure you want to delete this resource?')) {
      this.resourceService.deleteResource(id)
        .subscribe(() => {
          this.loadResources();
        });
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      console.log('Selected file:', this.selectedFile);

      if (!this.selectedResource) {
        const nameControl = this.resourceForm.get('name');
        const typeControl = this.resourceForm.get('resourceType');
        const isNameEmpty = !nameControl?.value?.toString().trim();
        const isTypeEmpty = !typeControl?.value?.toString().trim();

        if (isNameEmpty && isTypeEmpty) {
          const defaults = this.getDefaultsFromFilename(this.selectedFile.name);
          if (defaults.resourceName) {
            nameControl?.setValue(defaults.resourceName);
          }
          if (defaults.resourceType) {
            typeControl?.setValue(defaults.resourceType);
          }
        }
      }
    }
  }

  private getDefaultsFromFilename(fileName: string): { resourceName?: string; resourceType?: string } {
    const sanitizedName = fileName.split('\\').pop()?.split('/').pop() || fileName;
    const extension = sanitizedName.split('.').pop()?.toLowerCase();
    const defaults: { resourceName?: string; resourceType?: string } = {};

    if (extension === 'mid' || extension === 'midi') {
      defaults.resourceType = 'MIDI';
      const baseName = sanitizedName.replace(/\.[^/.]+$/, '');
      const voiceLabel = this.getVoiceLabelFromFilename(baseName);
      defaults.resourceName = voiceLabel ? `MIDI - ${voiceLabel}` : 'MIDI';
    } else if (extension === 'pdf') {
      defaults.resourceType = 'Scope';
      defaults.resourceName = 'Sheet Music';
    }

    return defaults;
  }

  private scrollToResourceForm(): void {
    const element = this.resourceFormCard?.nativeElement as HTMLElement | undefined;
    if (!element) {
      return;
    }

    requestAnimationFrame(() => {
      element.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
      this.scrollParentToElement(element);
    });
  }

  private scrollParentToElement(element: HTMLElement): void {
    const scrollParent = this.findScrollableParent(element);
    if (!scrollParent || scrollParent === document.body || scrollParent === document.documentElement) {
      window.scrollTo({
        top: window.scrollY + element.getBoundingClientRect().top - 16,
        behavior: 'smooth'
      });
      return;
    }

    const parentRect = scrollParent.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const offset = elementRect.bottom - parentRect.bottom;

    if (offset > 0) {
      scrollParent.scrollBy({ top: offset + 16, behavior: 'smooth' });
    }
  }

  private findScrollableParent(element: HTMLElement): HTMLElement | null {
    let parent = element.parentElement;
    const scrollRegex = /(auto|scroll|overlay)/;

    while (parent) {
      const style = window.getComputedStyle(parent);
      const overflowY = style.overflowY || style.overflow;
      if (scrollRegex.test(overflowY) && parent.scrollHeight > parent.clientHeight) {
        return parent;
      }
      parent = parent.parentElement;
    }

    return document.scrollingElement as HTMLElement | null;
  }

  private getVoiceLabelFromFilename(baseName: string): string | null {
    const match = /(?:_|-)([satb])([12])?$/i.exec(baseName);
    if (!match) {
      return null;
    }

    const voiceMap: Record<string, string> = {
      s: 'Soprano',
      a: 'Alto',
      t: 'Tenor',
      b: 'Bass'
    };

    const voice = voiceMap[match[1].toLowerCase()];
    if (!voice) {
      return null;
    }

    const part = match[2];
    return part ? `${voice} ${part}` : voice;
  }

  onIsHyperlinkChange(): void {
    const isHyperlink = this.resourceForm.get('isHyperlink')?.value;
    if (isHyperlink) {
      this.resourceForm.get('path')?.enable();
      this.selectedFile = null; // Clear the selected file if "Is Hyperlink" is true
      console.log('File selector disabled because "Is Hyperlink" is true.');
    } else {
      this.resourceForm.get('path')?.disable();
    }
  }

  saveResource(): void {
    console.log('Save button clicked'); // Debugging log to ensure the method is triggered

    console.log('test1');

    if (this.resourceForm.valid) {
      const resourceData: Resource = {
        ...this.resourceForm.value,
        songId: this.songId, // Ensure songId is included
        resourceEntity: 'Song', // Ensure resourceEntity is set
        id: this.selectedResource?.id || undefined // Include id if updating an existing resource
      };

      //console.log('Resource Data:', resourceData); // Log the data being sent

      if (!this.resourceForm.get('isHyperlink')?.value) {
        // Only update the path if a new file is selected
        if (this.selectedFile) {
          const folder = this.getSongResourceFolder();
          const cleanFolder = folder.replace(/^\/+/, '');
          const fileName = this.selectedFile.name;
          resourceData.path = `song/${cleanFolder}/${fileName}`;
        } else if (this.selectedResource && this.currentFile) {
          // Keep the existing file path if no new file is selected
          resourceData.path = this.selectedResource.path;
        }
        // If neither a new file nor a current file exists, path remains undefined
      };

      console.log('path = ', resourceData.path);

      // Retrieve the resourceFolder from the @Input() property
      //const resourceFolder = `/song/${this.getSongResourceFolder()}`;

      if (this.selectedResource) {
        // First, update the resource using the PUT method
        this.resourceService.updateResource(this.selectedResource.id, resourceData).subscribe({
          next: () => {
            console.log('Resource updated successfully'); // Debugging log for successful update

            // If "Is Hyperlink" is false and a file is selected, upload the file
            if (!this.resourceForm.get('isHyperlink')?.value && this.selectedFile) {
              console.log('Uploading file:', this.selectedFile.name); // Debugging log for file upload
              const formData = new FormData();
              formData.append('file', this.selectedFile);

              this.resourceService.uploadResource(formData, resourceData.path).subscribe({
                next: () => {
                  console.log('File uploaded successfully'); // Debugging log for successful upload
                  this.loadResources();
                  this.closeForm();
                },
                error: (error) => {
                  console.error('Error uploading resource:', error); // Log the error
                }
              });
            } else {
              this.loadResources();
              this.closeForm();
            }
          },
          error: (error) => {
            console.error('Error updating resource:', error); // Log the error
          }
        });
      } else {
        // Create a new resource if no selectedResource exists
        this.resourceService.createResource(resourceData).subscribe({
          next: (createdResource) => {
            console.log('Resource created successfully'); // Debugging log for successful creation

            // If "Is Hyperlink" is false and a file is selected, upload the file
            if (!this.resourceForm.get('isHyperlink')?.value && this.selectedFile) {
              console.log('Uploading file:', this.selectedFile.name); // Debugging log for file upload
              const formData = new FormData();
              formData.append('file', this.selectedFile);

              this.resourceService.uploadResource(formData, resourceData.path).subscribe({
                next: () => {
                  console.log('File uploaded successfully'); // Debugging log for successful upload
                  this.loadResources();
                  this.closeForm();
                },
                error: (error) => {
                  console.error('Error uploading resource:', error); // Log the error
                }
              });
            } else {
              this.loadResources();
              this.closeForm();
            }
          },
          error: (error) => {
            console.error('Error creating resource:', error); // Log the error
          }
        });
      }
    } else {
      console.warn('Form is invalid:', this.resourceForm.value); // Log invalid form data
    }
  }

  private getSongResourceFolder(): string {
    // Use the ResourceFolder field passed from the parent SongComponent
    if (!this.resourceFolder) {
      console.error('ResourceFolder is not available.');
      return 'default-folder'; // Fallback to 'default-folder' if not found
    }

    console.log('Fetched resourceFolder from @Input():', this.resourceFolder);
    return this.resourceFolder;
  }

  closeForm(): void {
    this.selectedResource = null;
    this.showResourceForm = false;
    this.resourceForm.reset();
    this.selectedFile = null;
    this.currentFile = null;
  }

  downloadResource(resource: Resource): void {
    if (!resource.isHyperlink) {
      if (!resource.path) {
        console.error('Resource path is empty or invalid:', resource);
        return;
      }
      this.resourceService.downloadResourceResponse(resource.path).subscribe({
        next: (res) => {
          const blob = res.body as Blob;
          if (!blob || blob.size === 0) {
            console.error('Downloaded file is empty. Check the backend response.');
            return;
          }
          const filename = this.extractFilename(res.headers.get('Content-Disposition'))
            || resource.path.split('/').pop()
            || resource.name
            || 'download';
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          window.URL.revokeObjectURL(url);
        },
        error: (error) => console.error('Error downloading resource:', error)
      });
    }
  }

  onResourceClick(event: Event, resource: Resource): void {
    event.preventDefault(); // Prevent default browser behavior
    event.stopPropagation(); // Stop event propagation

    console.log('Resource clicked:', resource); // Log the resource for debugging
    if (!resource.isHyperlink) {
      this.viewResource(resource);
    }
  }

  private viewResource(resource: Resource): void {
    if (!resource.path) {
      console.error('Resource path is empty or invalid:', resource);
      return;
    }
    const fallbackName = resource.path.split('/')?.pop() || resource.name || 'resource';
    this.resourceService.downloadResourceResponse(resource.path).subscribe({
      next: (res) => {
        const blob = res.body as Blob;
        if (!blob || blob.size === 0) {
          console.error('Downloaded file is empty. Check the backend response.');
          return;
        }
        const cd = res.headers.get('Content-Disposition');
        const serverFilename = this.extractFilename(cd) || fallbackName;
        // Prefer opening viewable types
        const inferredType = this.inferMimeType(serverFilename) || blob.type || 'application/octet-stream';
        const displayBlob = blob.type && blob.type !== '' ? blob : new Blob([blob], { type: inferredType });
        const viewable = inferredType.startsWith('image/') || inferredType === 'application/pdf' || inferredType.startsWith('text/') || inferredType === 'application/json' || inferredType.startsWith('audio/') || inferredType.startsWith('video/');
        const objectUrl = window.URL.createObjectURL(displayBlob);
        if (viewable) {
          const win = window.open(objectUrl, '_blank');
          if (!win) {
            console.warn('Popup blocked. Please allow popups for this site.');
          }
          setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
        } else {
          const a = document.createElement('a');
          a.href = objectUrl;
          a.download = serverFilename;
          a.click();
          window.URL.revokeObjectURL(objectUrl);
        }
      },
      error: (error) => console.error('Error fetching resource for viewing:', error)
    });
  }

  private inferMimeType(fileName: string): string {
    const ext = (fileName.split('.').pop() || '').toLowerCase();
    switch (ext) {
      case 'png': return 'image/png';
      case 'jpg':
      case 'jpeg': return 'image/jpeg';
      case 'gif': return 'image/gif';
      case 'webp': return 'image/webp';
      case 'svg': return 'image/svg+xml';
      case 'pdf': return 'application/pdf';
      case 'txt': return 'text/plain';
      case 'html':
      case 'htm': return 'text/html';
      case 'json': return 'application/json';
      case 'mp3': return 'audio/mpeg';
      case 'wav': return 'audio/wav';
      case 'mp4': return 'video/mp4';
      case 'mov': return 'video/quicktime';
      default: return 'application/octet-stream';
    }
  }

  private extractFilename(contentDisposition: string | null): string | null {
    if (!contentDisposition) return null;
    // content-disposition: attachment; filename="name.ext"; filename*=UTF-8''name.ext
    const filenameStarMatch = /filename\*=UTF-8''([^;\n]+)/i.exec(contentDisposition);
    if (filenameStarMatch && filenameStarMatch[1]) {
      try { return decodeURIComponent(filenameStarMatch[1]); } catch { /* ignore */ }
    }
    const filenameMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
    return filenameMatch && filenameMatch[1] ? filenameMatch[1] : null;
  }
}